import { Classes } from "@blueprintjs/core";
import { Classes as PopoverClasses } from "@blueprintjs/popover2";
import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";

import {
  HexButton,
  HexCheckbox,
  HexMenu,
  HexMenuDivider,
  HexMenuItem,
  HexPopover,
  HexTooltip,
} from "../../hex-components";
import {
  MinimalCategory,
  MinimalStatus,
} from "../../hooks/useProjectLabelsForHexVersion";
import { useGetCategoriesAndStatusesQuery } from "../../hooks/useProjectLabelsForHexVersion.generated";
import { ORG_ID } from "../../orgs";
import { AppliedCategories } from "../common/labels/AppliedCategories";
import { CategoryLabel } from "../common/labels/CategoryLabel";
import { StatusLabel } from "../common/labels/StatusLabel";
import { InfoIcon, SingleChevronDownIcon } from "../icons/CustomIcons";

const DROPDOWN_WIDTH = 120;
export const STATUS_LABEL = "status-label";
export const CATEGORIES_LABEL = "categories-label";

const LoadingLabels = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  height: 24px;
  padding-left: 3px;

  &::before,
  &::after {
    display: block;
    width: 60px;
    height: 3px;
    margin-right: 15px;

    background-color: ${({ theme }) => theme.activeColor};
    border-radius: ${({ theme }) => theme.borderRadius};

    content: "";
  }
`;

const LabelLayout = styled.div`
  display: flex;
  min-width: 0;
  margin-left: -5px;

  transition: background-color ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};
`;

const LabelSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: auto;
  min-width: 0;
  padding: 0;

  &.${STATUS_LABEL} {
    flex-shrink: 0;
  }

  &.${CATEGORIES_LABEL} {
    flex-shrink: 1;
  }

  .${PopoverClasses.POPOVER2_TARGET} {
    display: flex;
    min-width: 0;
  }
`;

const PickerButton = styled(HexButton)<{
  $readonly: boolean;
}>`
  justify-content: space-between;
  width: 100%;
  height: auto;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  outline: none;

  .${Classes.BUTTON_TEXT} {
    min-width: 0;

    &:not(:last-child) {
      margin-right: 3px;
    }
  }

  ${({ $readonly }) =>
    $readonly &&
    css`
      &&&.${Classes.BUTTON} {
        &&.${Classes.BUTTON}:not(.${Classes.DISABLED}):hover {
          background-color: transparent;
        }
      }
    `}
`;

const LabelOptionsList = styled(HexMenu)`
  min-width: ${DROPDOWN_WIDTH}px;
`;

const LabelOption = styled(HexMenuItem)`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

export const AppliedCategoryWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: -4px 0;
`;

const StyledCheckbox = styled(HexCheckbox)`
  display: flex;
  align-items: center;

  .${Classes.CONTROL_INDICATOR} {
    margin-top: 0;
  }
  .${PopoverClasses.POPOVER2_TARGET} {
    display: flex;
  }
`;

interface ProjectStatusProps {
  allStatuses: readonly MinimalStatus[];
  appliedStatus?: MinimalStatus | null;
  disabled?: boolean;
  muted?: boolean;
  onSelectStatus: (newStatus: MinimalStatus | null) => void;
  readonly?: boolean;
}

const ProjectStatus: React.FunctionComponent<ProjectStatusProps> = ({
  allStatuses,
  appliedStatus,
  disabled = false,
  muted = false,
  onSelectStatus,
  readonly = false,
}) => {
  const nonLibraryStatuses = useMemo(
    () => allStatuses.filter((o) => !o.inLibrary),
    [allStatuses],
  );

  const libraryStatuses = useMemo(
    () => allStatuses.filter((o) => o.inLibrary),
    [allStatuses],
  );

  if ((disabled || readonly) && !appliedStatus) {
    return null;
  }

  return (
    <LabelSection className={STATUS_LABEL}>
      <HexPopover
        captureDismiss={true}
        content={
          <LabelOptionsList>
            {nonLibraryStatuses.map((status) => {
              return (
                <LabelOption
                  key={status.name}
                  active={appliedStatus?.id === status.id}
                  text={
                    <StatusLabel
                      descriptionTooltipProps={{ placement: "right" }}
                      status={status}
                    />
                  }
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() => onSelectStatus(status)}
                />
              );
            })}
            {libraryStatuses.length > 0 && (
              <>
                <HexMenuDivider
                  title={
                    <HexTooltip
                      content={`${
                        libraryStatuses.length > 1
                          ? "These statuses"
                          : "This status"
                      } will add your published app to the Library.`}
                      hoverCloseDelay={200}
                      interactionKind="hover"
                      placement="right"
                    >
                      <div
                        css={`
                          display: flex;
                          align-items: center;
                        `}
                      >
                        <div
                          css={`
                            margin-right: 3px;
                          `}
                        >
                          In Library
                        </div>
                        <InfoIcon />
                      </div>
                    </HexTooltip>
                  }
                />
                {libraryStatuses.map((status) => {
                  return (
                    <LabelOption
                      key={status.name}
                      active={appliedStatus === status}
                      text={
                        <StatusLabel
                          descriptionTooltipProps={{ placement: "right" }}
                          status={status}
                        />
                      }
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={() => onSelectStatus(status)}
                    />
                  );
                })}
              </>
            )}
            {appliedStatus && (
              <>
                <HexMenuDivider />
                <LabelOption
                  intent="danger"
                  text="Clear status"
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() => onSelectStatus(null)}
                />
              </>
            )}
          </LabelOptionsList>
        }
        disabled={disabled || readonly}
        minimal={true}
        placement="bottom-start"
      >
        <PickerButton
          $readonly={readonly}
          className={Classes.INPUT}
          disabled={disabled}
          minimal={true}
          rightIcon={
            !appliedStatus ? <SingleChevronDownIcon iconSize={14} /> : null
          }
          small={true}
        >
          {appliedStatus ? (
            <StatusLabel muted={muted} status={appliedStatus} />
          ) : (
            "Add status"
          )}
        </PickerButton>
      </HexPopover>
    </LabelSection>
  );
};

interface ProjectCategoriesProps {
  allCategories: readonly MinimalCategory[];
  appliedCategories: readonly MinimalCategory[];
  disabled?: boolean;
  muted?: boolean;
  onSelectCategory: (category: MinimalCategory, applied: boolean) => void;
  readonly?: boolean;
}

export const ProjectCategories: React.FunctionComponent<
  ProjectCategoriesProps
> = ({
  allCategories,
  appliedCategories,
  disabled = false,
  muted = false,
  onSelectCategory,
  readonly = false,
}) => {
  const appliedCategoryIds = useMemo(
    () => appliedCategories.map((c) => c.id),
    [appliedCategories],
  );
  const isCategoryApplied = useCallback(
    (category: MinimalCategory) => {
      return appliedCategoryIds.includes(category.id);
    },
    [appliedCategoryIds],
  );

  if ((disabled || readonly) && appliedCategories.length === 0) {
    return null;
  }

  return (
    <LabelSection className={CATEGORIES_LABEL}>
      <HexPopover
        captureDismiss={true}
        content={
          <LabelOptionsList>
            {allCategories.map((category) => {
              return (
                <LabelOption
                  key={category.name}
                  // shouldDismissPopover={false}
                  text={
                    <StyledCheckbox
                      checked={isCategoryApplied(category)}
                      labelElement={
                        <CategoryLabel
                          category={category}
                          descriptionTooltipProps={{
                            placement: "right",
                          }}
                        />
                      }
                    />
                  }
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() =>
                    onSelectCategory(category, isCategoryApplied(category))
                  }
                />
              );
            })}
          </LabelOptionsList>
        }
        disabled={disabled || readonly}
        minimal={true}
        placement="bottom-start"
      >
        <PickerButton
          $readonly={readonly}
          className={Classes.INPUT}
          disabled={disabled}
          minimal={true}
          rightIcon={
            appliedCategories.length === 0 ? (
              <SingleChevronDownIcon iconSize={14} />
            ) : null
          }
          small={true}
        >
          {appliedCategories && appliedCategories.length > 0 ? (
            <AppliedCategoryWrapper>
              <AppliedCategories
                appliedCategories={appliedCategories}
                muted={muted}
                showFullName={false}
              />
            </AppliedCategoryWrapper>
          ) : (
            "Add categories"
          )}
        </PickerButton>
      </HexPopover>
    </LabelSection>
  );
};

interface ProjectLabelsProps {
  appliedCategories: readonly MinimalCategory[];
  appliedStatus: MinimalStatus | null;
  display?: "all" | "categories" | "status";
  disabled?: boolean;
  muted?: boolean;
  onSelectCategory: (category: MinimalCategory, applied: boolean) => void;
  onSelectStatus: (newStatus: MinimalStatus | null) => void;
  readonly?: boolean;
  className?: string;
}

export const ProjectLabels: React.FunctionComponent<ProjectLabelsProps> = ({
  appliedCategories,
  appliedStatus,
  className,
  disabled = false,
  display = "all",
  muted = false,
  onSelectCategory,
  onSelectStatus,
  readonly,
}) => {
  const { data, loading } = useGetCategoriesAndStatusesQuery({
    variables: { orgId: ORG_ID },
  });

  const allOrgCategories = useMemo(
    () => [...(data?.orgById.categories ?? [])],
    [data],
  );

  const allOrgStatuses = useMemo(
    () => [...(data?.orgById.statuses ?? [])],
    [data],
  );

  const preventNav = useCallback(
    (evt) => {
      if (!readonly) {
        evt.preventDefault();
      }
    },
    [readonly],
  );

  if (loading) {
    return <LoadingLabels />;
  } else if (allOrgStatuses.length === 0 && allOrgCategories.length === 0) {
    return null;
  }

  if (display === "categories" || allOrgStatuses.length === 0) {
    return (
      <div onClick={preventNav}>
        <ProjectCategories
          allCategories={allOrgCategories}
          appliedCategories={appliedCategories}
          disabled={disabled}
          muted={muted}
          readonly={readonly}
          onSelectCategory={onSelectCategory}
        />
      </div>
    );
  }

  if (display === "status" || allOrgCategories.length === 0) {
    return (
      <div onClick={preventNav}>
        <ProjectStatus
          allStatuses={allOrgStatuses}
          appliedStatus={appliedStatus}
          disabled={disabled}
          muted={muted}
          readonly={readonly}
          onSelectStatus={onSelectStatus}
        />
      </div>
    );
  }

  return (
    <LabelLayout className={className} onClick={preventNav}>
      <ProjectStatus
        allStatuses={allOrgStatuses}
        appliedStatus={appliedStatus}
        disabled={disabled}
        muted={muted}
        readonly={readonly}
        onSelectStatus={onSelectStatus}
      />
      <ProjectCategories
        allCategories={allOrgCategories}
        appliedCategories={appliedCategories}
        disabled={disabled}
        muted={muted}
        readonly={readonly}
        onSelectCategory={onSelectCategory}
      />
    </LabelLayout>
  );
};
